/**
 * Onboarding API — seam between frontend and backend.
 *
 * This module calls the backend onboarding endpoints via `ApiClient`.
 * The backend is the SOLE authority on all onboarding state, including:
 * - profile, objective, completions
 * - configuredAt (when first configuration happened)
 * - completionCelebrationSeen (whether user has seen the 100% completion modal)
 *
 * No local state persistence. The frontend is stateless.
 *
 * Backend contract:
 *   GET  /onboarding/state              → OnboardingStateDTO
 *   PUT  /onboarding/profile            → { profile, objective }
 *   PUT  /onboarding/objective          → { objective }
 *   POST /onboarding/event              → { eventType }
 *   POST /onboarding/celebration-seen   → {}
 */

import {
  ActionId,
  CompletionState,
  Objective,
  OnboardingEventType,
  Profile,
} from './onboarding.types';
import ApiClient from '../../methods/api/apiClient';

// ---------------------------------------------------------------------------
// Cache mémoire partagé (niveau module) — évite 4 appels identiques
// `GET /onboarding/state` au boot (Sidebar, useOnboardingCompletion,
// ExplainerManager, MarketplaceServicesSection). Invalidé à chaque mutation.
// `getState(true)` force un rafraîchissement réseau (utilisé par refreshState).
// ---------------------------------------------------------------------------
let _stateCache: OnboardingStateDTO | null = null;
let _stateCachedAt = 0;
let _stateInflight: Promise<OnboardingStateDTO> | null = null;
const STATE_CACHE_TTL_MS = 20000;

const _resetStateCache = () => {
  _stateCache = null;
  _stateCachedAt = 0;
};

const _defaultState = (): OnboardingStateDTO => ({
  profile: 'owner',
  objective: 'sell',
  completions: {},
  configuredAt: null,
  completionCelebrationSeen: false,
});

const _cloneState = (s: OnboardingStateDTO): OnboardingStateDTO => ({
  ...s,
  completions: { ...(s.completions || {}) },
});

// ---------------------------------------------------------------------------
// DTO — mirrors the backend response shape
// ---------------------------------------------------------------------------

export interface OnboardingStateDTO {
  profile: Profile;
  objective: Objective;
  completions: Partial<Record<ActionId, CompletionState>>;
  configuredAt?: string | null;
  /** True once the user has seen the 100%-completion celebration modal (backend-managed). */
  completionCelebrationSeen?: boolean;
  /** Explainer flags (backend-managed). Default to false when absent. */
  explainerSidebarDone?: boolean;
  explainerDashboardDone?: boolean;
  explainerTransactionOwnerDone?: boolean;
  explainerTransactionSearcherDone?: boolean;
}

// ---------------------------------------------------------------------------
// Public API object — calls backend endpoints directly
// ---------------------------------------------------------------------------

export const onboardingApi = {
  /** GET /onboarding/state — Backend returns all state including completionCelebrationSeen */
  getState: (force = false): Promise<OnboardingStateDTO> => {
    const now = Date.now();
    if (!force && _stateCache && now - _stateCachedAt < STATE_CACHE_TTL_MS) {
      return Promise.resolve(_cloneState(_stateCache));
    }
    if (_stateInflight) return _stateInflight;
    _stateInflight = ApiClient.get('/onboarding/state')
      .then((res) => {
        const data = res && res.success && res.data ? res.data : _defaultState();
        _stateCache = data;
        _stateCachedAt = Date.now();
        _stateInflight = null;
        return _cloneState(data);
      })
      .catch(() => {
        _stateInflight = null;
        return _defaultState();
      });
    return _stateInflight;
  },

  /** PUT /onboarding/profile — Backend manages configuredAt timestamp */
  updateProfile: (
    profile: Profile,
    objective: Objective,
    location?: { city?: string; country?: string }
  ): Promise<void> => {
    _resetStateCache();
    const body: any = { profile, objective };
    if (location?.city) body.locationCity = location.city;
    if (location?.country) body.locationCountry = location.country;
    return ApiClient.put('/onboarding/profile', body).then(() => {
      // Backend handled it — no local state needed
    }).catch(() => { /* non-blocking */ });
  },

  /**
   * POST /onboarding/explainer/done — Marks a given explainer tour as done.
   * type: 'sidebar' | 'dashboard' | 'transactionOwner' | 'transactionSearcher'
   */
  markExplainerDone: (type: string): Promise<void> => {
    _resetStateCache();
    return ApiClient.post('/onboarding/explainer/done', { type }).catch(() => { /* non-blocking */ });
  },

  /** PUT /onboarding/objective — Backend manages state */
  updateObjective: (objective: Objective): Promise<void> => {
    _resetStateCache();
    return ApiClient.put('/onboarding/objective', { objective }).catch(() => { /* non-blocking */ });
  },

  /**
   * POST /onboarding/celebration-seen
   * Backend sets the flag so the modal never shows again after 100% completion.
   */
  markCelebrationSeen: (): Promise<void> => {
    _resetStateCache();
    return ApiClient.post('/onboarding/celebration-seen', {}).catch(() => { /* non-blocking */ });
  },

  /**
   * POST /onboarding/event
   * Backend receives event, updates completions, returns updated state.
   */
  sendEvent: (eventType: OnboardingEventType): Promise<void> => {
    _resetStateCache();
    return ApiClient.post('/onboarding/event', { eventType }).then((res) => {
      if (res && res.success) {
        // Backend handled it — no local state needed
        return;
      }
      return Promise.resolve();
    }).catch(() => {
      // Non-blocking: user continues even if event dispatch failed
      return Promise.resolve();
    });
  },
};

// ---------------------------------------------------------------------------
// Standalone helpers — usable from any JS/JSX page without a React context
// ---------------------------------------------------------------------------

/**
 * Fire an onboarding event from any page or handler.
 * Fire-and-forget: no return value, errors are silently swallowed.
 *
 * Usage:
 *   import { fireOnboardingEvent } from '../../components/onboarding/onboarding.api';
 *   fireOnboardingEvent('property_published_sale');
 */
export function fireOnboardingEvent(eventType: OnboardingEventType): void {
  onboardingApi.sendEvent(eventType).catch(() => { /* non-blocking */ });
}
